import React, { Component } from "react";
import { TenantReqDetailsRequest } from "@eres/ejari-api/IApi";
import { EjariApi } from "@eres/ejari-api";
import { AttachmentInfo, History, TenantContractInfo } from "@eres/ejari-api/Entity/tenant";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import PageContent from "@my-dld/components/PageContent";
import { extractPropInfo } from "../../../component/PropertyHeader";
import {
	AlphaSortProcessor,
	EjariPropertyFromTenantRequest,
	getTenantRequestStatus,
	IDtoEnum,
	LicenseAuthoritiesEmiratesTransformer,
	LicSourceProcessor,
	populateLookupOnTo,
	StateConsumer
} from "@eres/ejari-api/Utils";
import { EjariDetails } from "../../PropertyDetails";
import { ImperialSystem, MetricSystem, UnitSystem } from "@my-dld/utils/UnitUtils";
import { dispatch, navigateWithInParent } from "@my-dld/utils/StoreUtils";
import Actions from "@my-dld/actions";
import { EjariProperty, EjariPropertyUsage, getTenantDocType, Lookup, TenantDocTypes, TenantDocumentLookup } from "@eres/ejari-api/Entity";
import Strings from "@my-dld/assets/strings";
import DetailsKV from "../../../component/DetailsKV";
import { formatAppDate } from "@my-dld/utils/Utils";
import { toFormattedCurrency } from "@eres/core/dist/Currency";
import {
	TenancyContactAction,
	TenantDocumentType,
	TenantRequestAction,
	TenantRequestStatus,
	TenantRequestUserType
} from "@eres/ejari-core/constants";
import { EjariLookUp } from "@eres/ejari-api/constants";
import { DateUtil, I8N, LocaleUtil, StringUtil } from "@eres/core";
import TenantRequestActions from "../../../component/TenantRequests/TenantRequestActions";
import OwnerRequestActions from "../../../component/TenantRequests/OwnerRequestActions";
import { resetTenantContractContext, TenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { getImagePreview } from "../../../component/TenantContract/TCDocument";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { MyTenancyRequestsBCItem, RequestsFromTenantBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import Switch from "@my-dld/components/Switch";
import { getAppServices } from "@10x/coreapp";
import HazardsDetails from "../../../component/TenantRequests/HazardsDetails";

interface Props extends RoutedViewProps<{ userTypeId: string, contractTypeId: string, contractId: string }> {

}

interface LookupState {
	Emirates?: Lookup[];
	LicenseStates?: Lookup[];
}

interface State extends LookupState {
	contract?: TenantContractInfo;
	property?: EjariProperty;
	unit: UnitSystem;
	canResubmit: boolean;
	documents: TenantDocumentLookup[];
}

class TenantRequestDetails extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			unit: getAppServices().storeService.getStore().getState().User.unit,
			canResubmit: false,
			documents: [],
		};
	}

	componentDidMount(): void {
		const { contract, property, canResubmit } = this.context;
		if ( contract && property && canResubmit ) {
			this.setState( {
				contract: contract,
				property: property,
				canResubmit: canResubmit,
			}, () => {
				this.loadLookups();
			} );
		} else {
			this.loadContract();
		}
	}

	onUnitSystemChange = ( isSqm: boolean ) => {
		this.setState( {
			unit: isSqm ? MetricSystem : ImperialSystem,
		}, () => {
			const newSystem = this.state.unit;
			dispatch( Actions.UNIT_CHANGED, newSystem );
		} );
	};

	render() {
		const { userTypeId } = this.props.match.params;
		const { unit, contract, property, documents, canResubmit } = this.state;
		const status = IDtoEnum<TenantRequestStatus>( TenantRequestStatus, contract?.Status )!;
		const isCommercial = ( property && property.EjariPropertyUsageId &&
			property.EjariPropertyUsageId !== EjariPropertyUsage.RESIDENTIAL );
		const isIndustrial = ( property && property.EjariPropertyUsageId === EjariPropertyUsage.INDUSTRIAL );
		let title = Strings.request_details;
		if ( property ) {
			const { val, lbl } = extractPropInfo( property );
			title = `${ title } - ${ lbl } : ${ val }`;
		}
		return <PageContent
			title={ title }
			hideQLinks={ true }
			titleExtra={ <>
				<Switch
					id={ "unitSystem" }
					onLabel={ Strings.sq_mt }
					offLabel={ Strings.sq_ft }
					value={ this.state.unit.Id === MetricSystem.Id }
					onChange={ this.onUnitSystemChange }/>
			</> }>
			<PageTitle
				title={ Strings.request_details } paths={ [ userTypeId === "" + TenantRequestUserType.TENANT ?
				MyTenancyRequestsBCItem : RequestsFromTenantBCItem ] }/>
			{ contract && <React.Fragment>
				<TenantRequestActions
					userTypeId={ userTypeId } contract={ contract }
					onActionComplete={ this.loadContract }
					canResubmit={ canResubmit }/>
				<OwnerRequestActions
					userTypeId={ userTypeId } contract={ contract }
					onActionComplete={ this.loadContract }/>
				<div className="section-title">
					<h5>{ Strings.status }</h5>
				</div>
				<div className="flex-grid">
					<div>
						<span>{ Strings.contract_status }</span>
						<span>{ getTenantRequestStatus( status ) }</span>
					</div>
				</div>
				<Comments contract={ contract }/>
				{ property && <EjariDetails
					showTitle={ true }
					showUnitSwitch={ false }
					property={ property }
					unit={ unit }
					onUnit={ isSqm => {
						const update = { unit: ImperialSystem };
						if ( isSqm ) update.unit = MetricSystem;
						this.setState( update, () => {
							dispatch( Actions.UNIT_CHANGED, this.state.unit );
						} );
					} }/> }
				{
					!StringUtil.isEmptyStringOrNull( contract.DewaMoveoutRequestno )
					&& !StringUtil.isEmptyStringOrNull( contract.DewaMoveoutDate )
					&& !StringUtil.isEmptyStringOrNull( contract.DewaMoveinDate ) &&
					<MoveOutDetails
						userTypeId={ userTypeId }
						contract={ contract } onEdit={ this.editMoveOutInfo }/>
				}
				<TenantDetails userTypeId={ userTypeId } contract={ contract } isCommercial={ !!isCommercial }/>
				<ContractDetails
					userTypeId={ userTypeId } contract={ contract }
					onEdit={ this.editContractInfo }/>
				{ isCommercial && <CommercialDetails
					userTypeId={ userTypeId } state={ this.state }
					contract={ contract } onEdit={ this.editCommercialInfo }/> }
				{ isIndustrial && <HazardsDetails
					userTypeId={ userTypeId } contract={ contract }
					onEdit={ this.editHazardInfo }/> }
				<DocumentDetails
					userTypeId={ userTypeId } contract={ contract } documents={ documents }
					onEdit={ this.editDocumentInfo }/>
				<RemarkDetails
					userTypeId={ userTypeId } contract={ contract }
					onEdit={ this.editRemarksInfo }/>
			</React.Fragment> }
		</PageContent>;
	}

	private loadContract = () => {
		showLoading();
		const { contractTypeId, contractId } = this.props.match.params;
		const req: TenantReqDetailsRequest = {
			ContractType: Number( contractTypeId ),
			ContractID: Number( contractId ),
		};
		EjariApi.getTenantRequestDetail( req ).then( ( resp: TenantContractInfo ) => {
			hideLoading();
			const property = ( resp.Properties && resp.Properties.length > 0 ) ? resp.Properties[ 0 ] : undefined;
			const ejariProperty = property ? EjariPropertyFromTenantRequest( property ) : undefined;
			this.setState( {
				contract: resp,
				property: ejariProperty,
			}, () => {
				if ( resp && ejariProperty ) {
					resetTenantContractContext();
					updateTenantContractContext( {
						contract: resp, property: ejariProperty,
						action: TenancyContactAction.UPDATE_CONTRACT
					} );
					this.loadLookups();
				}
			} );
		} ).catch( e => {
			hideLoading();
			showErrorMessage( e );
		} );
	};

	private loadLookups() {
		populateLookupOnTo( EjariLookUp.EMIRATES, StateConsumer( this, "Emirates" ), LicenseAuthoritiesEmiratesTransformer, LicSourceProcessor );
		populateLookupOnTo( EjariLookUp.LICENSE_STATES, StateConsumer( this, "LicenseStates" ), undefined, AlphaSortProcessor );
		this.loadTenantAttachments();
	}

	private loadTenantAttachments() {
		const { contract } = this.state;
		if ( contract ) {
			const reqTypeId = ( contract.RequestActionType === TenantRequestAction.TERMINATE ) ? TenantDocumentType.TERMINATE :
				TenantDocumentType.PERSON;
			EjariApi.getTenantContractDocuments( reqTypeId ).then( ( resp: TenantDocumentLookup[] ) => {
				resp.forEach( ( docLooup: TenantDocumentLookup ) => {
					if ( contract.Attachments && contract.Attachments.length > 0 ) {
						contract.Attachments.find( ( attachment: AttachmentInfo ) => {
							if ( attachment.DocumentID === docLooup.Id ) {
								if ( docLooup.Docs == undefined ) {
									docLooup.Docs = [];
								}
								docLooup.Docs.push( attachment );
							}
						} );
					}
				} );
				updateTenantContractContext( { documentInfo: { Documents: resp } } );
				this.setState( { documents: resp } );
			} ).catch( e => {
				hideLoading();
				showErrorMessage( e );
			} );
		}
	}

	private editCommercialInfo = () => {
		navigateWithInParent( "tenant/request", "edit/commercial" );
	};

	private editContractInfo = () => {
		navigateWithInParent( "tenant/request", "edit/contract" );
	};

	private editDocumentInfo = () => {
		navigateWithInParent( "tenant/request", "edit/documents" );
	};

	private editRemarksInfo = () => {
		navigateWithInParent( "tenant/request", "edit/remarks" );
	};

	private editHazardInfo = () => {
		navigateWithInParent( "tenant/request", "edit/hazards" );
	};

	private editMoveOutInfo = () => {
		navigateWithInParent( "tenant/request", "edit/moveout" );
	};
}

function Comments( props: { contract: TenantContractInfo } ) {
	const { contract } = props;
	if ( contract && contract.History && contract.History.length > 0 ) {
		return <div className="card mb-4 card-ui comment-section">
			<div className="card-header">
				<span>{ Strings.comments }</span>
			</div>
			<div className="card-body">
				<ul className="list-group">
					{ contract.History.map( ( history: History, index: number ) => {
						return <li key={ "comment_history_" + index } className="list-group-item comment-list">
							<span className="user-type">{ Strings.owner }</span>
							<span className="user-name">
                            <span><i
	                            className="far fa-user"/> { I8N.isRTL() ? history.CreatedByNameAr : history.CreatedByNameEn }</span>
                            <span className="comment-date" title={ Strings.date_and_time }>
                                <i className="far fa-clock"/>
	                            { DateUtil.formatDate( history.CreationDate!.asDate(), "DD/MM/YYYY hh:MM A" ) }
                            </span>
                        </span>
							<span className="user-comment">{ history.Remarks }</span>
						</li>;
					} ) }
				</ul>

			</div>
		</div>;

	}
	return null;
}

function TenantDetails( props: { userTypeId: string, contract: TenantContractInfo, isCommercial?: boolean } ) {
	const { contract, isCommercial } = props;
	if ( contract.Tenants && contract.Tenants.length > 0 ) {
		const tenant = contract.Tenants[ 0 ];
		const person = tenant.Person;
		return <div className="mb-4">
			<div className="section-title">
				<h5>{ Strings.tenant_details }</h5>
			</div>
			<div className="flex-grid">
				<DetailsKV label={ Strings.tenant_name_ar } value={ tenant.TenantArabicName }/>
				<DetailsKV label={ Strings.tenant_name_en } value={ tenant.TenantEnglishName }/>
				{ person && <React.Fragment>
					<DetailsKV label={ Strings.emirates_id } value={ person?.EmiratesId }/>
					<DetailsKV label={ Strings.mobile_number } value={ person?.Mobile }/>
				</React.Fragment> }
				<DetailsKV label={ Strings.email } value={ tenant.Email }/>
				{ contract.TotalCoTenants !== undefined && !isCommercial &&
					<DetailsKV label={ Strings.no_of_co_occupants } value={ contract.TotalCoTenants }/> }
			</div>
		</div>;
	}
	return null;
}

function CommercialDetails( props: { state: State, userTypeId: string, contract: TenantContractInfo, onEdit: () => void } ) {
	const { state, userTypeId, contract, onEdit } = props;
	if ( contract.Tenants && contract.Tenants.length > 0 ) {
		const tenant = contract.Tenants[ 0 ];
		const person = tenant.Person;
		const emirate = getLicenseEmirate( state.Emirates, person?.LicenseIssuerEmirate );
		const authorities = emirate ? emirate.Children : undefined;
		return <div className="card card-ui mb-4">
			<div className="card-header">
				<span>{ Strings.commercial_details }</span>
				{ userTypeId === "" + TenantRequestUserType.TENANT &&
					contract.Status === TenantRequestStatus.REQUEST_FOR_UPDATE &&
					contract.RequestActionType !== TenantRequestAction.TERMINATE &&
					<button className="btn btn-link btn-sm" type="button" onClick={ onEdit }>
						<i className="fas fa-pencil-alt me-2"/>{ Strings.edit }</button>
				}
			</div>
			<div className="card-body">
				<div className="flex-grid">
					<DetailsKV
						label={ Strings.lic_issuer_emirate }
						value={ emirate ? LocaleUtil.getLocalValue( emirate ) : undefined }/>
					<DetailsKV
						label={ Strings.lic_issuer_authority }
						value={ getLicenseData( authorities, Number( person?.LicenseIssuer ) ) }/>
					<DetailsKV
						label={ Strings.license_state }
						value={ getLicenseData( state.LicenseStates, person?.LicenseStateId ) }/>
					<DetailsKV label={ Strings.license_number } value={ person?.LicenseNo }/>
				</div>
			</div>
		</div>;
	}
	return null;
}

function MoveOutDetails( props: { userTypeId: string, contract: TenantContractInfo, onEdit: () => void } ) {
	const { userTypeId, contract, onEdit } = props;
	return <div className="card card-ui mb-4">
		<div className="card-header">
			<span>{ Strings.move_out_information }</span>
		</div>
		<div className="card-body">
			<div className="flex-grid">
				<DetailsKV label={ Strings.move_in_date } value={ formatAppDate( contract.DewaMoveinDate ) }/>
				<DetailsKV label={ Strings.move_out_date } value={ formatAppDate( contract.DewaMoveoutDate ) }/>
				<DetailsKV label={ Strings.move_out_ejari_contract_number } value={ contract.DewaMoveoutRequestno }/>
			</div>
		</div>
	</div>;
}

function ContractDetails( props: { userTypeId: string, contract: TenantContractInfo, onEdit: () => void } ) {
	const { userTypeId, contract, onEdit } = props;
	return <div className="card card-ui mb-4">
		<div className="card-header">
			<span>{ Strings.contract_details }</span>
			{ userTypeId === "" + TenantRequestUserType.TENANT &&
				contract.Status === TenantRequestStatus.REQUEST_FOR_UPDATE &&
				contract.RequestActionType !== TenantRequestAction.TERMINATE &&
				<button className="btn btn-link btn-sm" type="button" onClick={ onEdit }>
					<i className="fas fa-pencil-alt me-2"/>{ Strings.edit }</button>
			}
		</div>
		<div className="card-body">
			<div className="flex-grid">
				<DetailsKV
					label={ Strings.contract_number }
					value={ `${ contract.ContractNumber } (V-${ contract.VersionNumber })` }/>
				<DetailsKV label={ Strings.start_date } value={ formatAppDate( contract.ContractStartDate ) }/>
				<DetailsKV label={ Strings.end_date } value={ formatAppDate( contract.ContractEndDate ) }/>
				{ contract.GraceStartDate &&
					<DetailsKV label={ Strings.grace_start_date } value={ formatAppDate( contract.GraceStartDate ) }/> }
				{ contract.GraceEndDate &&
					<DetailsKV label={ Strings.grace_end_date } value={ formatAppDate( contract.GraceEndDate ) }/> }
				<DetailsKV
					label={ Strings.contract_amount }
					value={ toFormattedCurrency( Number( contract.ContractAmount! ) ) + " " + Strings.aed }/>
				{ contract.SecurityDeposit != undefined &&
					<DetailsKV
						label={ Strings.security_deposit }
						value={ toFormattedCurrency( Number( contract.SecurityDeposit! ) ) + " " + Strings.aed }/> }
			</div>
		</div>
	</div>;
}

function DocumentDetails( props: { userTypeId: string, contract: TenantContractInfo, documents: TenantDocumentLookup[], onEdit: () => void } ) {
	const { userTypeId, contract, documents, onEdit } = props;
	if ( documents && documents.length > 0 ) {
		return <div className="card card-ui mb-4">
			<div className="card-header">
				<span>{ Strings.documents }</span>
				{ userTypeId === "" + TenantRequestUserType.TENANT &&
					contract.Status === TenantRequestStatus.REQUEST_FOR_UPDATE &&
					<button className="btn btn-link btn-sm" type="button" onClick={ onEdit }>
						<i className="fas fa-pencil-alt me-2"/>{ Strings.edit }</button>
				}
			</div>
			<div className="card-body pb-3">
				{ documents.map( ( docLookup: TenantDocumentLookup ) => {
					return <div key={ "doc_parent_" + docLookup.Id }>
						<label>{ LocaleUtil.getLocalValue( docLookup ) }</label>
						{ docLookup.Docs?.map( ( attachment: AttachmentInfo ) => {
							const docType = getTenantDocType( attachment );
							const preview = ( docType === TenantDocTypes.PDF ) ?
								<i className="far fa-file-pdf"/> : getImagePreview( attachment );
							return <a
								key={ "doc_" + attachment.Identity }
								onClick={ () => {
									if ( !isEmptyStringOrNull( attachment.DocumentURL ) )
										window.open( attachment.DocumentURL );
								} }>
								<div>{ preview } <span>{ attachment.FileName }</span></div>
							</a>;
						} ) }
					</div>;
				} ) }
			</div>
		</div>;
	}
	return null;
}

function RemarkDetails( props: { userTypeId: string, contract: TenantContractInfo, onEdit: () => void } ) {
	const { userTypeId, contract, onEdit } = props;
	return <div className="card card-ui">
		<div className="card-header">
			<span>{ Strings.remarks }</span>
			{ userTypeId === "" + TenantRequestUserType.TENANT &&
				contract.Status === TenantRequestStatus.REQUEST_FOR_UPDATE &&
				contract.RequestActionType !== TenantRequestAction.TERMINATE &&
				<button className="btn btn-link btn-sm" type="button" onClick={ onEdit }>
					<i className="fas fa-pencil-alt me-2"/>{ Strings.edit }</button>
			}
		</div>
		<div className="card-body pb-3">
			<span>{ contract.Remarks }</span>
		</div>
	</div>;
}

function getLicenseEmirate( lookups?: Lookup[], id?: number ) {
	if ( lookups && lookups.length > 0 && id ) {
		return lookups.find( ( lookup: Lookup ) => lookup.Id === id );
	}
	return undefined;
}

function getLicenseData( lookups?: Lookup[], id?: number ) {
	if ( lookups && lookups.length > 0 && id !== undefined ) {
		const sellLookUp = lookups.find( ( lookup: Lookup ) => lookup.Id === id );
		return LocaleUtil.getLocalValue( sellLookUp );
	}
	return undefined;
}

export default TenantRequestDetails;
